import { useEffect } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";

/**
 * `useNavigate()` only works inside a component under `<BrowserRouter>`, but
 * `customerAppsHostAdapter.navigate` (`HostAdapter.navigate`'s real implementation here) is
 * called from package components that must stay usable without a Router above them (Cantemo
 * Portal mounts have none) — see `HostAdapter.ts`'s own `navigate` doc comment for why that's
 * still true even though `react-router-dom` is a real peer dependency of the package now (Monday
 * S2.7). Same module-level-singleton shape `HostAdapter.ts` already uses for
 * `setHostAdapter`/`getHostAdapter`: `<NavigateBridge>` stashes the live `navigate` function here
 * once, and `navigateFromHost` reads it back.
 */
let navigateRef: NavigateFunction | null = null;

/** Falls back to a real navigation if `<NavigateBridge>` hasn't mounted yet (or has unmounted) —
 *  still gets the user where they're going, just without the client-side route change. Shouldn't
 *  happen in practice: `<NavigateBridge>` mounts once, at the router root, before any route
 *  content that could call this. */
export function navigateFromHost(
  path: string,
  options?: { replace?: boolean }
): void {
  if (!navigateRef) {
    window.location.assign(path);
    return;
  }
  navigateRef(path, options);
}

/** Mount once, inside `<BrowserRouter>`, near `AppRouter`'s root. Renders nothing. */
export function NavigateBridge(): null {
  const navigate = useNavigate();
  useEffect(() => {
    navigateRef = navigate;
    return () => {
      navigateRef = null;
    };
  }, [navigate]);
  return null;
}

/** This package's own tests import this directly so one test's mount can't leak into another's —
 *  same reasoning as `HostAdapter.ts`'s `resetHostAdapterForTests`. */
export function resetNavigateBridgeForTests(): void {
  navigateRef = null;
}
